import { Box } from "@mui/system";
import { DateTimeValidationError } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import { BaseFieldProps } from "./FieldProps";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import { PRODUCT_STATUS } from "../../models/Product";
import { useFormStore } from "../../stores/formStore";
import CalendarIcon from "../icons/ui-actions/CalendarIcon";

type Props<TFieldValues extends FieldValues> = {
  dateNotBefore?: Path<TFieldValues>;
  disablePast?: boolean;
} & BaseFieldProps<TFieldValues>;

const DateTimeField = <TFieldValues extends FieldValues>({
  name,
  label,
  disabled = false,
  disabledForEdit = false,
  dateNotBefore,
  disablePast = false,
}: Props<TFieldValues>) => {
  const [minDate, setMinDate] = useState<Dayjs | undefined>(undefined);
  const [dateError, setDateError] = useState<DateTimeValidationError>(null);

  const { control, clearErrors, watch, setError } =
    useFormContext<TFieldValues>();
  const { field } = useController({
    name,
    control,
  });
  const startDateSelected = dateNotBefore && watch(dateNotBefore);
  const status = useFormStore(state => state.status);

  const errorMessage: Record<string, string> = {
    minDate: "Start date must be before End date",
    disablePast: "This date cannot be in the past",
    invalidDate: "Your date is not valid",
  };

  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);

  const handleChange = (date: Dayjs | null) => {
    const formDate = date?.format("DD/MM/YYYY HH:mm:ss");
    field.onChange(
      formDate as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    );
  };

  useEffect(() => {
    if (dateNotBefore) {
      setMinDate(dayjs(startDateSelected, "DD/MM/YYYY HH:mm:ss"));
    }
    if (!dateError) {
      clearErrors(field.name);
    } else {
      setError(field.name, {
        message: errorMessage[dateError],
      });
    }
  }, [startDateSelected, dateError]);

  return (
    <Box data-testid={`form-${label}-DATE`} width={"-webkit-fill-available"}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          {...field}
          value={field.value ? dayjs(field.value, "DD/MM/YYYY HH:mm:ss") : null}
          onChange={handleChange}
          disabled={fieldIsDisabled}
          label={label}
          views={["year", "month", "day", "hours"]}
          format="DD/MM/YYYY HH:mm:ss"
          slots={{
            openPickerIcon: CalendarIcon,
          }}
          onError={newError => setDateError(newError)}
          slotProps={{
            textField: {
              helperText: dateError && errorMessage[dateError],
            },
          }}
          disablePast={
            disablePast &&
            [PRODUCT_STATUS.DRAFT, PRODUCT_STATUS.PENDING_APPROVAL].includes(
              status || PRODUCT_STATUS.DRAFT,
            )
          }
          minDateTime={minDate}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DateTimeField;
